import { useEffect, useMemo, useState } from 'react'
import { salesAPI } from '../services/api'
import SaleDetailModal from './SaleDetailModal'
import { SALE_STATUSES, QUOTE_STATUSES, INVOICE_STATUSES, EMPTY_SALE } from './admin/constants'
import './AdminDashboard.sales.css'

const L = {
  en: {
    quotes: 'Quotes',
    invoices: 'Invoices',
    quotesDesc: 'Create and send quotes to clients.',
    invoicesDesc: 'Create and track invoices.',
    add: 'Add',
    client: 'Client',
    product: 'Product / Service',
    amount: 'Amount',
    date: 'Date',
    notes: 'Notes',
    status: 'Status',
    search: 'Search…',
    empty: 'No records yet.',
    view: 'View',
    remove: 'Remove',
    saved: 'Saved.',
    error: 'Error',
  },
  fr: {
    quotes: 'Devis',
    invoices: 'Factures',
    quotesDesc: 'Créez et envoyez des devis clients.',
    invoicesDesc: 'Créez et suivez les factures.',
    add: 'Ajouter',
    client: 'Client',
    product: 'Produit / Service',
    amount: 'Montant',
    date: 'Date',
    notes: 'Notes',
    status: 'Statut',
    search: 'Rechercher…',
    empty: 'Aucun enregistrement.',
    view: 'Voir',
    remove: 'Supprimer',
    saved: 'Enregistré.',
    error: 'Erreur',
  },
}

export default function WorkerBilling({ mode = 'quotes', lang = 'fr' }) {
  const t = L[lang] || L.fr
  const isQuotes = mode === 'quotes'
  const defaultStatus = isQuotes ? 'quote' : 'invoice'
  const statusFilter = isQuotes ? QUOTE_STATUSES : INVOICE_STATUSES

  const [sales, setSales] = useState([])
  const [form, setForm] = useState({ ...EMPTY_SALE, status: defaultStatus })
  const [search, setSearch] = useState('')
  const [msg, setMsg] = useState('')
  const [selected, setSelected] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    salesAPI.getAll().then(setSales).catch((err) => setMsg(`${t.error}: ${err.message}`))
  }, [t.error])

  useEffect(() => {
    setForm({ ...EMPTY_SALE, status: defaultStatus })
    setSelected(null)
    setMsg('')
  }, [mode, defaultStatus])

  const visible = useMemo(() => {
    let rows = sales.filter((s) => statusFilter.includes(s.status))
    if (search.trim()) {
      const q = search.toLowerCase()
      rows = rows.filter((s) =>
        [s.client, s.product, s.reference, s.notes, s.status, s.amount]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      )
    }
    return rows
  }, [sales, search, statusFilter])

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }))

  const handleAdd = async (e) => {
    e.preventDefault()
    if (!form.client.trim() || !form.product.trim() || !form.amount.trim()) {
      setMsg(lang === 'fr' ? 'Client, produit et montant requis.' : 'Client, product and amount required.')
      return
    }
    setBusy(true)
    setMsg('')
    const st = form.status || defaultStatus
    const prefix = isQuotes || st === 'quote' || st === 'accepted' ? 'QUOTE' : 'INV'
    const reference = `${prefix}-${Date.now().toString(36).toUpperCase()}`
    try {
      const created = await salesAPI.create({
        ...form,
        status: st,
        reference,
        client: form.client.trim(),
        product: form.product.trim(),
        amount: form.amount.trim(),
      })
      setSales((prev) => [created, ...prev])
      setForm({ ...EMPTY_SALE, status: defaultStatus })
      setMsg(`${reference} — ${t.saved}`)
    } catch (err) {
      setMsg(`${t.error}: ${err.message}`)
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async (id) => {
    try {
      await salesAPI.delete(id)
      setSales((prev) => prev.filter((s) => s.id !== id))
      setSelected((prev) => (prev && prev.id === id ? null : prev))
    } catch (err) {
      setMsg(`${t.error}: ${err.message}`)
    }
  }

  const handleStatus = async (id, status) => {
    try {
      const updated = await salesAPI.update(id, { status })
      setSales((prev) => prev.map((s) => (s.id === id ? updated : s)))
      setSelected((prev) => (prev && prev.id === id ? updated : prev))
    } catch (err) {
      setMsg(`${t.error}: ${err.message}`)
    }
  }

  const handleSave = async (patch) => {
    if (!selected) return null
    const updated = await salesAPI.update(selected.id, patch)
    setSales((prev) => prev.map((s) => (s.id === selected.id ? updated : s)))
    setSelected(updated)
    return updated
  }

  return (
    <div className="wd-section">
      <div className="wd-section-header">
        <h2>{isQuotes ? t.quotes : t.invoices}</h2>
        <p>{isQuotes ? t.quotesDesc : t.invoicesDesc}</p>
      </div>

      <form className="wd-card" onSubmit={handleAdd} style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label>
            {t.client}
            <input value={form.client} onChange={set('client')} required />
          </label>
          <label>
            {t.product}
            <input value={form.product} onChange={set('product')} required />
          </label>
          <label>
            {t.amount}
            <input value={form.amount} onChange={set('amount')} required />
          </label>
          <label>
            {t.date}
            <input type="date" value={form.date} onChange={set('date')} />
          </label>
          <label>
            {t.status}
            <select value={form.status} onChange={set('status')}>
              {SALE_STATUSES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label[lang] || o.label.en}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t.notes}
            <input value={form.notes} onChange={set('notes')} />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="wd-btn primary" type="submit" disabled={busy}>
            {t.add}
          </button>
          {msg && <span className="wd-muted">{msg}</span>}
        </div>
      </form>

      <div className="wd-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <strong>
            {isQuotes ? t.quotes : t.invoices} ({visible.length})
          </strong>
          <input
            type="search"
            placeholder={t.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 200 }}
          />
        </div>
        {visible.length === 0 ? (
          <p className="wd-muted">{t.empty}</p>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Ref</th>
                  <th>{t.client}</th>
                  <th>{t.product}</th>
                  <th>{t.amount}</th>
                  <th>{t.status}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((s) => (
                  <tr key={s.id} className="dash-row-clickable" onClick={() => setSelected(s)}>
                    <td className="dash-ref">{s.reference || '—'}</td>
                    <td>{s.client}</td>
                    <td>{s.product}</td>
                    <td>
                      <strong>{s.amount}</strong>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <select
                        className="dash-status-select-sm"
                        value={s.status}
                        onChange={(e) => handleStatus(s.id, e.target.value)}
                      >
                        {SALE_STATUSES.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label[lang] || o.label.en}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td onClick={(e) => e.stopPropagation()} className="dash-table-actions">
                      <button type="button" className="wd-btn secondary" onClick={() => setSelected(s)}>
                        {t.view}
                      </button>
                      <button type="button" className="wd-btn danger" onClick={() => handleDelete(s.id)}>
                        {t.remove}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <SaleDetailModal
          sale={selected}
          lang={lang}
          onClose={() => setSelected(null)}
          onSave={handleSave}
          onDelete={handleDelete}
          onStatusChange={handleStatus}
        />
      )}
    </div>
  )
}
